import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../infrastructure/state/store";
import { getUniqueObjects } from "../utils/get-unique-values-by-prop-path";
import {
  IActivityHistoryItem,
  IActivityHistoryItemCollection,
  IHistoryDataImporterRules,
  TSearchActivityHistoryItemPredicate,
} from "./domain/model";
import { CommonFilterSpec } from "./domain/specs/common-filter-spec";
import { CustomFilterSpec } from "./domain/specs/custom-filter-spec";
import { DiagnosticCodeSpec } from "./domain/specs/diagnostics-filter-spec";
import { SearchStringSpec } from "./domain/specs/search-string-spec";
import { ComposedTypeSpec } from "./domain/specs/type-spec";
import { UpFilterSpec } from "./domain/specs/up-filter-spec";
import { IActivityHistoryCommonFilter, IActivityHistoryCustomFilterGroup, IDiagnosticWithRelations } from "./filter";
import { activityHistoryCommonFiltersSelector } from "./filter/common-filters/selectors";
import { activityHistoryCustomFilterGroupsSelector } from "./filter/custom-filters/selectors";
import { diagnosticFilterEnabledValuesSelector } from "./filter/diagnostic-filters/selectors";
import { upFilterEnabledValuesSelector } from "./filter/up-filters/selectors";
import { groupActivityHistoryItems } from "./list/group-history-items/group-history-items";
import { sortGroupsByMostRecentDate } from "./list/sort-history-items-by-date/sort-history-items-by-date";
import { activityHistorySearchStringSelector } from "./search/selectors";

export const activityHistoryCollectionsSelector = (state: RootState) => state.activityHistoryCollections.collections;

export const historyDataImporterRulesByCollectionIdSelector = createSelector(
  activityHistoryCollectionsSelector,
  (collections: Record<string, IActivityHistoryItemCollection>): Record<string, IHistoryDataImporterRules> => {
    const rules: Record<string, IHistoryDataImporterRules> = {};
    for (const [id, collection] of Object.entries(collections)) {
      if (collection?.historyDataImporterRules) {
        rules[id] = collection.historyDataImporterRules;
      }
    }
    return rules;
  },
);

const mapItemsWithMetadata = (
  items: IActivityHistoryItem[],
  componentFactory: any,
  type: string,
  searchPredicate: TSearchActivityHistoryItemPredicate,
) => {
  return items.map(
    (item) =>
      ({
        ...item,
        searchPredicate,
        componentFactory,
        type,
      }) as IActivityHistoryItem,
  );
};

export const activityHistoryMappedItems = createSelector(
  activityHistoryCollectionsSelector,
  (collections: Record<string, IActivityHistoryItemCollection>) => {
    return Object.values(collections).flatMap((collection: IActivityHistoryItemCollection) => {
      return mapItemsWithMetadata(collection.items, collection.componentFactory, collection.id, collection.searchPredicate);
    });
  },
);

export const customFilterGroupsWithItemsSelector = createSelector(
  activityHistoryCustomFilterGroupsSelector,
  activityHistoryCollectionsSelector,
  (filterGroups: IActivityHistoryCustomFilterGroup[], collections: Record<string, IActivityHistoryItemCollection>) => {
    return filterGroups.filter((g: IActivityHistoryCustomFilterGroup) => {
      const filterCollection = collections[g.id] as IActivityHistoryItemCollection;
      return filterCollection?.items && filterCollection.items.length > 0;
    });
  },
);
export const customFilterGroupsWithOptionsSelector = createSelector(
  activityHistoryCustomFilterGroupsSelector,
  activityHistoryCollectionsSelector,
  (filterGroupsWithItems: IActivityHistoryCustomFilterGroup[], collections: Record<string, IActivityHistoryItemCollection>) => {
    const result = filterGroupsWithItems.map((filterGroup: IActivityHistoryCustomFilterGroup) => {
      const collection = collections[filterGroup.id] as IActivityHistoryItemCollection;
      return {
        ...filterGroup,
        filters: filterGroup.filters.map((f) => {
          return {
            ...f,
            options:
              (!f.singleOption
                ? collection?.items &&
                  getUniqueObjects<IActivityHistoryItem>(collection.items, f.propPathValue, f.propsPathDescription, f.title)
                : []) || [],
          };
        }),
      };
    });
    return result;
  },
);

export const activityHistoryGroupedItems = createSelector(
  activityHistoryMappedItems,
  activityHistorySearchStringSelector,
  activityHistoryCommonFiltersSelector,
  activityHistoryCustomFilterGroupsSelector,
  diagnosticFilterEnabledValuesSelector,
  upFilterEnabledValuesSelector,
  (
    items: IActivityHistoryItem[],
    searchString: string,
    commonFilters: IActivityHistoryCommonFilter[],
    customFilters: IActivityHistoryCustomFilterGroup[],
    diagnosticFilterEnabledValues: IDiagnosticWithRelations[],
    upFilterEnabledValues: string[],
  ) => {
    // Create the filter spec chain ONCE instead of creating it for every item
    const commonFilterSpec = new CommonFilterSpec(commonFilters);
    const typeSpec = new ComposedTypeSpec(customFilters);
    const customFilterSpec = new CustomFilterSpec(customFilters);
    const diagnosticSpec = new DiagnosticCodeSpec(diagnosticFilterEnabledValues);
    const upSpec = new UpFilterSpec(upFilterEnabledValues);

    const filteredItems = items.filter((item: IActivityHistoryItem) => {
      // Create search spec per item since it needs item.searchPredicate
      const searchSpec = new SearchStringSpec(searchString, item.searchPredicate);

      return searchSpec
        .and(commonFilterSpec)
        .and(typeSpec)
        .and(customFilterSpec)
        .and(diagnosticSpec)
        .and(upSpec)
        .isSatisfiedBy(item);
    });

    const groupedItems = groupActivityHistoryItems(filteredItems);
    const sortedByMostRecentDate = sortGroupsByMostRecentDate(groupedItems);
    return sortedByMostRecentDate;
  },
);
